import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, computed } from "vue";
import { useForm } from "vee-validate";
import { useErrors } from "@/ts/utils/api-functions";
import * as Yup from "yup";
import FormInput from "@/ts/components/form/input.vue";
import FormFile from "@/ts/components/form/file.vue";
import Submit from "@/ts/components/form/submit.vue";
import { nl } from "yup-locales";
import axios from "axios";
import { serialize } from "object-to-formdata";
import { checkIfFilesAreTooBig } from "@/ts/utils/file-validation";
import { useReCaptcha } from "vue-recaptcha-v3";

interface Props {
    subject?: string;
    class?: string;
    isCv?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'contact-form',
  props: {
    subject: { type: String, required: false },
    class: { type: String, required: false },
    isCv: { type: Boolean, required: false }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const apiErrors = useErrors();
const success = ref(false);
const loading = ref(false);
const height = ref(0);
const component = ref<HTMLDivElement>();
const reCaptcha = useReCaptcha();

const props = __props;

Yup.setLocale(nl);

const { handleSubmit, isSubmitting } = useForm({
    initialValues: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        comment: "",
        subject: props.subject && props.subject != "" ? props.subject : "Nieuw bericht",
        files: [],
        source_url: window.location.href,
    },
    validationSchema: Yup.object({
        subject: Yup.string().required().label("Onderwerp"),
        first_name: Yup.string().required().label("Voornaam"),
        last_name: Yup.string().required().label("Achternaam"),
        email: Yup.string().email().required().label("E-mailadres"),
        phone: Yup.string().label("Telefoonnummer"),
        comment: Yup.string().required().label("Bericht"),
        files: Yup.array().test("is-big-file", "Totale bestandsgrootte mag maximum 10MB zijn", checkIfFilesAreTooBig),
        source_url: Yup.string(),
    }),
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    loading.value = true;
    try {
        await reCaptcha!.recaptchaLoaded();
        const token = await reCaptcha!.executeRecaptcha("login");
        const formData = serialize(values);
        formData.append("g-recaptcha-response", token);
        formData.append("is_job", props.isCv ? "1" : "0");
        await axios.post("/api/contact", formData);
        success.value = true;
        height.value = 0;
        setTimeout(() => {
            resetForm();
        }, 500);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

onMounted(() => {
    height.value = component.value!.offsetHeight;
});

const lang = computed(() => {
    return document.documentElement.lang;
});

const translations = computed(() => {
    switch (lang.value) {
        case "fr":
            return {
                first_name: "Prénom",
                last_name: "Nom",
                email: "Adresse électronique",
                phone: "Numéro de téléphone",
                comment: props.isCv ? "Brève motivation" : "Message",
                cv: "CV",
                privacy_policy:
                    'Si vous souhaitez en savoir plus sur la façon dont <strong>RIMA Trans</strong> traite ces données personnelles,<br>veuillez consulter notre  <a href="/docs/privacy-policy.pdf" target="_blank" class="text-primary hover:no-underline underline">politique en matière de confidentialité</a>.',
                submit: "Envoyer",
                recaptcha:
                    'Ce site est protégé par reCAPTCHA,<br>veuillez vous référer à la <a class="text-primary hover:no-underline underline" href="https://policies.google.com/privacy" rel="noopener" target="_blank">déclaration de confidentialité</a> et aux <a class="text-primary hover:no-underline underline" href="https://policies.google.com/terms" rel="noopener" target="_blank">conditions de service</a> de Google.',
                success: props.isCv ? "Merci pour votre candidature! Nous le regardons et vous faisons savoir quelque chose rapidement." : "Merci! Nous avons bien reçu votre message. Nous vous contacterons dès que possible.",
            };

        case "en":
            return {
                first_name: "First name",
                last_name: "Surname",
                email: "E-mail",
                phone: "Phone",
                comment: props.isCv ? "Motivation" : "Message",
                cv: "CV",
                privacy_policy: 'If you want to know more about how <strong>RIMA Trans</strong> processes this personal data,<br>please check our <a href="/docs/privacy-policy.pdf" target="_blank" class="text-primary hover:no-underline underline">privacy policy</a>.',
                submit: "Submit",
                recaptcha:
                    'This site is protected by reCAPTCHA,<br>and the Google <a class="text-primary hover:no-underline underline" href="https://policies.google.com/privacy" rel="noopener" target="_blank">Privacy Policy</a> and <a class="text-primary hover:no-underline underline" href="https://policies.google.com/terms" rel="noopener" target="_blank">Terms of Service</a> apply.',
                success: props.isCv ? "Thanks for your application! We will review your application and come back to you shortly." : "Thanks! We have received your message. We will contact you as soon as possible.",
            };

        default:
            return {
                first_name: "Voornaam",
                last_name: "Achternaam",
                email: "E-mailadres",
                phone: "Telefoonnummer",
                comment: props.isCv ? "Korte motivatie" : "Bericht",
                cv: "CV",
                privacy_policy:
                    'Wil je meer weten over hoe <strong>RIMA Trans</strong> deze persoonsgegevens verwerkt,<br>check dan onze <a href="/docs/privacy-policy.pdf" target="_blank" class="text-primary hover:no-underline underline">privacyverklaring</a>.',
                submit: "Verzenden",
                recaptcha:
                    'Deze website is beschermd met reCAPTCHA,<br>zie hiervoor de <a class="text-primary hover:no-underline underline" href="https://policies.google.com/privacy" rel="noopener" target="_blank">privacyverklaring</a> en <a class="text-primary hover:no-underline underline" href="https://policies.google.com/terms" rel="noopener" target="_blank">servicevoorwaarden</a> van Google.',
                success: props.isCv ? "Bedankt voor je sollicitatie! We bekijken ‘m en laten je snel iets weten." : "Bedankt! We hebben je bericht goed ontvangen. We nemen zo snel mogelijk contact met je op.",
            };
    }
});

const __returned__ = { apiErrors, success, loading, height, component, reCaptcha, props, handleSubmit, isSubmitting, onSubmit, lang, translations, FormInput, FormFile, Submit }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})